import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  useEffect(() => {
    // Check if token exists in URL
    if (!token) {
      setError('Ungültiger Reset-Link. Bitte fordere einen neuen Link an.');
      setTokenValid(false);
    }
  }, [token]);

  const validatePassword = (password) => {
    if (password.length < 6) {
      return 'Das Passwort muss mindestens 6 Zeichen lang sein';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    // Validate passwords
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          newPassword: password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.error || 'Ein Fehler ist aufgetreten');
        if (data.error && (data.error.includes('expired') || data.error.includes('Invalid'))) {
          setTokenValid(false);
        }
      }
    } catch (err) {
      setError('Netzwerkfehler. Bitte versuche es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-8">
            <div className="flex justify-center mb-6">
              <img 
                src="https://www.2kbundesliga.de/wp-content/uploads/2022/01/cropped-cropped-2kbuli-header_06.png" 
                alt="2K Bundesliga Logo"
                className="h-20 w-auto object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            
            <h1 className="text-3xl font-bold mb-6 text-center text-red-600 dark:text-red-400">Link ungültig</h1>
            
            <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-6">
              {error || 'Dieser Reset-Link ist ungültig oder abgelaufen.'}
            </div>

            <div className="text-center space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                Bitte fordere einen neuen Reset-Link an:
              </p>
              <Link
                to="/forgot-password"
                className="inline-block bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-300"
              >
                Neuen Reset-Link anfordern
              </Link>
              <div className="mt-4">
                <Link 
                  to="/login" 
                  className="text-red-600 dark:text-red-400 hover:text-red-700 dark:text-red-400 font-medium"
                >
                  ← Zurück zum Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-md mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-8">
          <div className="flex justify-center mb-6">
            <img 
              src="https://www.2kbundesliga.de/wp-content/uploads/2022/01/cropped-cropped-2kbuli-header_06.png" 
              alt="2K Bundesliga Logo"
              className="h-20 w-auto object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          
          <h1 className="text-3xl font-bold mb-6 text-center">Neues Passwort erstellen</h1>
          
          {message && (
            <div className="bg-green-100 dark:bg-green-900/30 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-400 px-4 py-3 rounded mb-4">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <p>{message}</p>
                  <p className="text-sm mt-1">Du wirst in wenigen Sekunden zur Login-Seite weitergeleitet...</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {!message && (
            <>
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-center">
                Bitte gib dein neues Passwort ein. Es muss mindestens 6 Zeichen lang sein.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">
                    Neues Passwort
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-red-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    required
                    disabled={loading}
                    placeholder="Mindestens 6 Zeichen"
                    minLength="6"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">
                    Passwort bestätigen
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-red-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    required
                    disabled={loading}
                    placeholder="Passwort wiederholen"
                    minLength="6"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Wird aktualisiert...' : 'Passwort aktualisieren'}
                </button>
              </form>
            </>
          )}

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:text-red-400 font-medium"
            >
              ← Zurück zum Login
            </Link>
          </div>

          {!message && (
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 dark:border-yellow-700 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Sicherheitshinweis
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-400">
                    <p>Dieser Reset-Link kann nur einmal verwendet werden und ist 60 Minuten gültig.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;